/**
 * Chat Layout Tailwind Constants
 * 
 * Semantic naming for all chat layout patterns.
 * These constants eliminate nested flexbox complexity and provide
 * a single source of truth for the chat UI structure.
 */

// Main layout containers
export const CHAT_LAYOUT = {
  // Main page container - flex row on desktop, column on mobile
  page: 'flex flex-col md:flex-row bg-background',

  // Sidebar container
  sidebar: 'hidden md:flex md:flex-col border-r border-border bg-card',
  sidebarContent: 'flex flex-col flex-1 overflow-hidden',

  // Main chat area - takes remaining space
  chatArea: 'flex-1 flex flex-col overflow-hidden w-full min-w-0',

  // Chat interface - messages + input container
  chatInterface: 'flex flex-col flex-1 w-full bg-background',

  // Message list container - grows to fill available space
  messageListContainer: 'flex-1 overflow-hidden',

  // Input section - fixed at bottom. `pb-[env(safe-area-inset-bottom)]` keeps
  // the input above the iOS Safari home-indicator bar; the inner wrapper still
  // applies its own padding for visual breathing room. Falls back to 0 on
  // browsers without the env() var.
  inputSection: 'border-t border-border bg-background flex-shrink-0 pb-[env(safe-area-inset-bottom)]',
  inputWrapper: 'w-full p-4',

  // Message item - individual message row
  messageItem: 'flex items-start gap-3 w-full',
  messageItemUser: 'justify-end px-4 py-2',
  messageItemBot: 'justify-start',

  // Message bubble - the actual message content. `break-words` + `whitespace-pre-wrap`
  // keep long URLs / code / emoji from horizontally scrolling the chat on mobile.
  messageBubble: 'px-4 py-2 rounded-lg break-words whitespace-pre-wrap',
  messageBubbleUser: 'max-w-[85%] sm:max-w-xl bg-primary text-primary-foreground',
  messageBubbleBot: 'w-full text-foreground',

  // Reading measure for a bot ANSWER. The bubble stays full-width (tool rows,
  // tables and artifacts want the room) but prose does not: an answer set across
  // a 1800px monitor loses the line on every wrap. ~78ch is the usual comfortable
  // band. Applies to the answer only — never to the activity strip, whose rows
  // are already dense and single-line.
  answerMeasure: 'max-w-[78ch]',
};

// Responsive utility classes
export const RESPONSIVE = {
  mobileOnly: 'md:hidden',
  desktopOnly: 'hidden md:block',
  mobileHidden: 'hidden md:flex',
};

// Common spacing patterns
export const SPACING = {
  gap: 'gap-3',
  gapSmall: 'gap-2',
  gapLarge: 'gap-4',
  padding: 'p-4',
  paddingSmall: 'p-2',
  paddingLarge: 'p-6',
  paddingX: 'px-4',
  paddingY: 'py-2',
};

// Avatar styles
export const AVATARS = {
  container: 'w-8 h-8 rounded-full flex-shrink-0',
  image: 'w-8 h-8 rounded-full object-cover flex-shrink-0',
  initials: 'w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-sm flex-shrink-0 text-white font-semibold text-sm',
};

// Common interactive states
export const INTERACTIVE = {
  hover: 'hover:bg-muted',
  active: 'active:opacity-60',
  disabled: 'disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed',
  focus: 'focus:outline-none focus:ring-2 focus:ring-primary',
  transition: 'transition-colors',
};

// Border and divider styles
export const BORDERS = {
  divider: 'border-b border-border',
  dividerRight: 'border-r border-border',
  card: 'border border-border',
};

// Scrolling styles
export const SCROLL = {
  container: 'overflow-y-auto',
  hidden: 'overflow-hidden',
  thin: 'scrollbar-thin scrollbar-thumb-primary/30',
};

// Header styles. `pl-14 md:pl-6` on mobile leaves room for the fixed
// `MobileMenuButton` (top-left) so the floating button doesn't sit on top of
// the logo/title. `gap-3` keeps the title clear of the actions menu.
export const HEADER = {
  chatHeader: 'border-b border-border pl-14 md:pl-6 pr-6 py-4 flex justify-between items-center gap-3 bg-card shadow-sm',
  // `truncate` (with `min-w-0` on the flex parent) prevents long titles from
  // pushing ChatActions off-screen on narrow viewports.
  chatTitle: 'text-xl font-semibold text-card-foreground truncate',
  headerIcon: 'w-8 h-8 rounded-full object-cover flex-shrink-0',
};

// Chat wrapper styles
export const WRAPPER = {
  chatActiveChatState: 'flex flex-col flex-1',
  chatSidebarContainer: 'flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-primary/30 scrollbar-track-transparent',
};
